import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { FileCode2, Loader2, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { PageHeader } from '@/components/common/PageHeader';
import { ErrorState } from '@/components/common/Feedback';
import { LoadingRows } from '@/components/common/Feedback';
import { PoolConfigCard } from '@/components/cgnat/PoolConfigCard';
import { MappingsTable } from '@/components/cgnat/MappingsTable';
import { ReverseLookupCard } from '@/components/cgnat/ReverseLookupCard';
import { ConfigExportDialog } from '@/components/cgnat/ConfigExportDialog';
import { usePool, useGenerateMappings } from '@/hooks/use-cgnat';

export const Route = createFileRoute('/_auth/cgnat/$poolId')({
  component: PoolDetailRoute,
});

function PoolDetailRoute() {
  const { poolId } = Route.useParams();

  const poolQuery = usePool(poolId);
  const generateMut = useGenerateMappings(poolId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const pool = poolQuery.data;
  const hasMappings = (pool?.mappings_count ?? 0) > 0;

  function triggerGenerate() {
    if (hasMappings) {
      // Show confirm dialog before overwriting
      setConfirmOpen(true);
    } else {
      void runGenerate();
    }
  }

  async function runGenerate() {
    setConfirmOpen(false);
    try {
      const result = await generateMut.mutateAsync();
      toast.success(`Generados ${result.generated} mappings`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al generar mappings';
      toast.error(msg);
    }
  }

  if (poolQuery.isLoading) {
    return (
      <div className="p-6">
        <LoadingRows rows={6} />
      </div>
    );
  }

  if (poolQuery.error || !pool) {
    return (
      <div className="p-6">
        <ErrorState error={poolQuery.error ?? new Error('Pool no encontrado')} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <PageHeader
        title={pool.name}
        description={`${pool.private_prefix} → ${pool.public_prefix}`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setExportOpen(true)}
            >
              <FileCode2 className="size-3.5" />
              Exportar config
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={triggerGenerate}
              disabled={generateMut.isPending}
            >
              {generateMut.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Generando…
                </>
              ) : hasMappings ? (
                <>
                  <RefreshCw className="size-3.5" />
                  Regenerar mappings
                </>
              ) : (
                <>
                  <Zap className="size-3.5" />
                  Generar mappings
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Body: main content + sticky sidebar */}
      <div className="flex min-h-0 flex-1 gap-0">
        {/* Main column */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
          <PoolConfigCard pool={pool} />
          <Separator />
          <MappingsTable poolId={poolId} poolName={pool.name} />
        </div>

        {/* Sticky right sidebar (lg+) */}
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-l border-border p-4 lg:block">
          <ReverseLookupCard />
        </aside>
      </div>

      {/* Confirm regenerate dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerar mappings</DialogTitle>
            <DialogDescription>
              Este pool ya tiene{' '}
              <strong>{pool.mappings_count ?? 0}</strong> mappings generados.
              Regenerar sobrescribirá todos los mappings existentes. ¿Continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={generateMut.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => { void runGenerate(); }}
              disabled={generateMut.isPending}
            >
              {generateMut.isPending ? 'Regenerando…' : 'Regenerar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export config dialog */}
      <ConfigExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        poolId={poolId}
        poolName={pool.name}
      />
    </div>
  );
}
